import type {
  ApiEnvelope,
  DiskDto,
  RecorderStatusDto,
  RecordingDto,
  SessionDto,
  SettingsDto,
  StreamHealthDto,
} from '../shared/api-types.js';

/**
 * Typed API client. Every server call goes through here so error handling,
 * the response envelope and the 401 redirect live in exactly one place.
 */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Raised on 401 so the shell can drop back to the login screen. */
export class UnauthorizedError extends ApiError {
  constructor(message: string) {
    super('unauthorized', message, 401);
    this.name = 'UnauthorizedError';
  }
}

type RequestOptions = {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
};

const GENERIC_ERROR = 'Something went wrong. Please try again.';

const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const method = options.method ?? 'GET';

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch {
    // Network-level failure: no envelope to read.
    throw new ApiError('network_error', 'Could not reach the server. Check your connection.', 0);
  }

  let envelope: ApiEnvelope<T> | null = null;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch {
    envelope = null;
  }

  if (response.status === 401) {
    throw new UnauthorizedError(envelope?.error?.message ?? 'Please log in to continue.');
  }

  if (!response.ok || envelope === null || envelope.success !== true) {
    throw new ApiError(
      envelope?.error?.code ?? 'server_error',
      envelope?.error?.message ?? GENERIC_ERROR,
      response.status,
    );
  }

  return envelope.data as T;
};

/** Same as `request` but also surfaces the envelope's meta block. */
const requestWithMeta = async <T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta: Record<string, unknown> | undefined }> => {
  const response = await fetch(path, {
    method: options.method ?? 'GET',
    credentials: 'same-origin',
    headers: options.body === undefined ? {} : { 'content-type': 'application/json' },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  }).catch(() => null);

  if (response === null) {
    throw new ApiError('network_error', 'Could not reach the server. Check your connection.', 0);
  }

  const envelope = (await response.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (response.status === 401) {
    throw new UnauthorizedError(envelope?.error?.message ?? 'Please log in to continue.');
  }
  if (!response.ok || envelope === null || envelope.success !== true) {
    throw new ApiError(
      envelope?.error?.code ?? 'server_error',
      envelope?.error?.message ?? GENERIC_ERROR,
      response.status,
    );
  }
  return { data: envelope.data as T, meta: envelope.meta as Record<string, unknown> | undefined };
};

export const api = {
  session: () => request<SessionDto>('/api/session'),
  login: (password: string) =>
    request<SessionDto>('/api/login', { method: 'POST', body: { password } }),
  logout: () => request<{ loggedOut: boolean }>('/api/logout', { method: 'POST' }),
  logoutAll: () => request<{ loggedOut: boolean }>('/api/logout-all', { method: 'POST' }),

  recorderStatus: () => request<RecorderStatusDto>('/api/recordings/status'),
  startRecording: (label?: string) =>
    request<RecordingDto>('/api/recordings/start', {
      method: 'POST',
      body: label === undefined ? {} : { label },
    }),
  stopRecording: () => request<RecordingDto>('/api/recordings/stop', { method: 'POST' }),

  recordings: () => request<RecordingDto[]>('/api/recordings'),
  renameRecording: (id: string, label: string) =>
    request<RecordingDto>(`/api/recordings/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { label },
    }),
  deleteRecording: (id: string) =>
    request<{ deleted: boolean }>(`/api/recordings/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  disk: () => request<DiskDto>('/api/disk'),
  settings: () => requestWithMeta<SettingsDto>('/api/settings'),
  saveSettings: (body: { streamLink?: string; maxRecordingHours?: number | null }) =>
    requestWithMeta<SettingsDto>('/api/settings', { method: 'PUT', body }),
  streamHealth: () => request<StreamHealthDto>('/api/stream-health'),
  refreshStream: () =>
    request<{ changed: boolean; health: StreamHealthDto }>('/api/stream-refresh', {
      method: 'POST',
    }),
};

export type { RecordingDto, RecorderStatusDto, DiskDto, SettingsDto, StreamHealthDto };
